"use client"

// Shared A/B-test exposure primitive.
//
// Assignment happens in Next.js middleware (deterministic IP+UA bucketing into a
// `vvN` cookie), never through PostHog's flag API, so PostHog only counts a
// person as enrolled when we manually capture `$feature_flag_called`. Every
// experiment used to hand-roll that: register super property, capture the
// exposure, fire `split_test`, each with its own module-level dedupe Set,
// which let the legs drift apart between experiments.
//
// ONE EXPERIMENT PER EVENT. A visitor can be enrolled in several live tests at
// once; each fires its own `split_test` event carrying exactly one
// `experiment` / `variant` pair. Nothing is ever batched, coalesced or
// concatenated across experiments, and the dedupe keys are per
// `flag:variant`: a global "already exposed this pageload" gate would swallow
// the second experiment's event.

import { registerPostHogProperties, trackPostHog } from "../scripts/posthog"
import { trackAction } from "../trackActions"

interface ExposureArgs {
  /** PostHog feature-flag key for the experiment. */
  flag: string
  /** The arm the visitor experienced. */
  variant: string
  /** Extra PostHog super properties to register alongside `$feature/<flag>`. */
  postHogProperties?: Record<string, unknown>
  /** Extra properties for the `split_test` event only. */
  eventProperties?: Record<string, unknown>
}

// Manual capture bypasses PostHog's built-in per-session `$feature_flag_called`
// dedup, so re-fires across remounts are gated here. Keyed by `flag:variant` so
// a visitor whose arm changes mid-session (a QA override, or vv13's test →
// bailout demote) still fires once for the new arm.
//
// The two legs keep SEPARATE Sets on purpose. `fireCheckoutExposure` fires the
// PostHog leg early (ordered ahead of the checkout `$pageview`, DEV-9883)
// and a shared Set would then make the later `Vv5ExposureBeacon` mount
// early-return, losing that visitor's `split_test` / Bloomreach exposure
// entirely.
const firedPostHogExposures = new Set<string>()
const firedTrackedExposures = new Set<string>()

/**
 * Enrol the visitor in a PostHog experiment: register the variant as a super
 * property, then capture the exposure event.
 *
 * Registers FIRST so this exposure and every later event (`begin_checkout`,
 * `purchase`) carries `$feature/<flag>`. That property is what PostHog's
 * experiment engine reads to attribute conversions to an arm.
 *
 * No readiness gate: `registerPostHogProperties` and `trackPostHog` queue
 * internally via `withPostHogClient`. A `posthog-ready` listener here would
 * reintroduce a lost-exposure race: mark the dedupe Set → unmount before ready
 * → cleanup removes the listener → every later mount early-returns on the Set
 * and the visitor is never enrolled.
 *
 * Use this directly only when the Bloomreach / datalake leg must be skipped
 * (see `checkoutExposure.ts`); otherwise call `trackExperimentExposure`.
 *
 * @param args - The experiment, the visitor's arm, and optional super properties.
 */
export const firePostHogExposure = ({
  flag,
  variant,
  postHogProperties,
}: ExposureArgs) => {
  const key = `${flag}:${variant}`
  if (firedPostHogExposures.has(key)) {
    return
  }
  firedPostHogExposures.add(key)

  registerPostHogProperties({
    [`$feature/${flag}`]: variant,
    ...postHogProperties,
  })
  trackPostHog("$feature_flag_called", {
    $feature_flag: flag,
    $feature_flag_response: variant,
  })
}

/**
 * Signal experiment exposure to every destination: PostHog (super property +
 * `$feature_flag_called`) and the `split_test` event that fans out to
 * Bloomreach, GA and the datalake.
 *
 * The `split_test` payload stays exactly as the beacons sent it: one
 * `experiment` / `variant` pair and whatever per-experiment extras the call site
 * passes. No membership properties are added to it, and no customer properties
 * are set on the Bloomreach profile.
 *
 * Callers keep their own eligibility gates (path checks, QA-override skips,
 * market checks): those encode per-experiment decisions and deliberately stay at
 * the call site.
 *
 * @param args - The experiment, the visitor's arm, and optional per-leg properties.
 */
export const trackExperimentExposure = (args: ExposureArgs) => {
  firePostHogExposure(args)

  const { flag, variant, eventProperties } = args
  const key = `${flag}:${variant}`
  if (firedTrackedExposures.has(key)) {
    return
  }
  firedTrackedExposures.add(key)

  trackAction("split_test", {
    experiment: flag,
    variant,
    ...eventProperties,
  })
}
